import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import type { GhostCursorEasing, GhostCursorMotionOptions, GhostCursorOrigin, GhostCursorPhase, GhostCursorPoint, GhostCursorState, GhostCursorTarget, UseGhostCursorOptions, UseGhostCursorReturn } from "./types";
const GHOST_CURSOR_TARGET_ACTIVE_CLASS_NAME = "vc-ghost-cursor-target-active";
const DEFAULT_VIEWPORT_PADDING = 72;
const MIN_TRAVEL_MS = 360;
const MAX_TRAVEL_MS = 720;
const ARRIVAL_PULSE_MS = 180;
const DEFAULT_IDLE_HIDE_MS = 5000;
const DEFAULT_SCROLL_SETTLE_MS = 220;
const STEP_HOLD_MS = 260;
const FAST_BATCH_MIN_TRAVEL_MS = 180;
const FAST_BATCH_MAX_TRAVEL_MS = 320;
const FAST_BATCH_PULSE_MS = 80;
const FAST_BATCH_FINAL_HOLD_MS = 200;
type ResolvedCursorStop = {
  element: HTMLElement | null;
  pulseElement: HTMLElement | null;
  point: GhostCursorPoint;
};
function wait(ms: number) {
  return new Promise<void>(resolve => {
    window.setTimeout(resolve, ms);
  });
}
function waitForNextAnimationFrame() {
  if (typeof window === "undefined" || typeof window.requestAnimationFrame !== "function") {
    return Promise.resolve();
  }
  return new Promise<void>(resolve => {
    window.requestAnimationFrame(() => {
      resolve();
    });
  });
}
function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
function createPoint(x: number, y: number): GhostCursorPoint {
  return {
    x,
    y
  };
}
function normalizeMotionOptions(options?: GhostCursorMotionOptions) {
  return {
    easing: options?.easing ?? "smooth",
    from: options?.from ?? "previous"
  } satisfies Required<GhostCursorMotionOptions>;
}
function getViewportFallbackPoint(): GhostCursorPoint {
  if (typeof window === "undefined") {
    return {
      x: 0,
      y: 0
    };
  }
  return {
    x: Math.max(window.innerWidth - 84, 0),
    y: Math.max(window.innerHeight - 84, 0)
  };
}
function getElementPoint(element: HTMLElement): GhostCursorPoint {
  const rect = element.getBoundingClientRect();
  const isTextEntry = element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;
  if (isTextEntry) {
    return {
      x: rect.left + Math.min(28, rect.width * 0.18),
      y: rect.top + rect.height / 2
    };
  }
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2
  };
}
function resolveTargetStop(target: GhostCursorTarget): ResolvedCursorStop | null {
  const element = target.element ?? null;
  const point = target.point ?? (element ? getElementPoint(element) : null);
  if (!point) {
    return null;
  }
  return {
    element,
    pulseElement: target.pulseElement ?? element,
    point
  };
}
function isOutsidePaddedViewport(element: HTMLElement, viewportPadding: number) {
  const rect = element.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  return rect.top < viewportPadding || rect.left < viewportPadding || rect.bottom > viewportHeight - viewportPadding || rect.right > viewportWidth - viewportPadding;
}
function getTravelDuration(from: GhostCursorPoint, to: GhostCursorPoint) {
  const distance = Math.hypot(to.x - from.x, to.y - from.y);
  return clamp(MIN_TRAVEL_MS + distance * 0.22, MIN_TRAVEL_MS, MAX_TRAVEL_MS);
}
function getFastBatchTravelDuration(from: GhostCursorPoint, to: GhostCursorPoint) {
  const distance = Math.hypot(to.x - from.x, to.y - from.y);
  return clamp(FAST_BATCH_MIN_TRAVEL_MS + distance * 0.14, FAST_BATCH_MIN_TRAVEL_MS, FAST_BATCH_MAX_TRAVEL_MS);
}
function getBatchScrollTarget(elements: HTMLElement[]) {
  if (elements.length === 0) {
    return null;
  }
  const centers = elements.map(element => {
    const rect = element.getBoundingClientRect();
    return {
      element,
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
  });
  const centroid = centers.reduce((point, center) => ({
    x: point.x + center.x / centers.length,
    y: point.y + center.y / centers.length
  }), {
    x: 0,
    y: 0
  });
  return centers.reduce((closest, candidate) => {
    if (!closest) {
      return candidate;
    }
    const closestDistance = Math.hypot(closest.x - centroid.x, closest.y - centroid.y);
    const candidateDistance = Math.hypot(candidate.x - centroid.x, candidate.y - centroid.y);
    return candidateDistance < closestDistance ? candidate : closest;
  }, centers[0]!).element;
}
export function useGhostCursor(t0) {
  const $ = _c(37);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    idleHideMs: t2,
    scrollSettleMs: t3,
    viewportPadding: t4
  } = t1;
  const idleHideMs = t2 === undefined ? DEFAULT_IDLE_HIDE_MS : t2;
  const scrollSettleMs = t3 === undefined ? DEFAULT_SCROLL_SETTLE_MS : t3;
  const viewportPadding = t4 === undefined ? DEFAULT_VIEWPORT_PADDING : t4;
  const [cursorState, setCursorState] = useState(_temp);
  const trackedPointerRef = useRef(null);
  const scriptedPointerRef = useRef(null);
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = Promise.resolve();
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const queueRef = useRef(t5);
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const activeTargetsRef = useRef(t6);
  const hideTimerRef = useRef(null);
  const reducedMotionRef = useRef(false);
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (hideTimerRef.current !== null) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const clearHideTimer = t7;
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      if (activeTargetsRef.current.length === 0) {
        return;
      }
      for (const element of activeTargetsRef.current) {
        element.classList.remove(GHOST_CURSOR_TARGET_ACTIVE_CLASS_NAME);
      }
      activeTargetsRef.current = [];
    };
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const clearActiveTargets = t8;
  let t9;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setCursorState(_temp2);
    };
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const hideAllCursors = t9;
  let t10;
  if ($[7] !== idleHideMs) {
    t10 = () => {
      clearHideTimer();
      hideTimerRef.current = window.setTimeout(() => {
        hideAllCursors();
      }, idleHideMs);
    };
    $[7] = idleHideMs;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const scheduleHide = t10;
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      clearHideTimer();
      clearActiveTargets();
      hideAllCursors();
    };
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const dismissCursors = t11;
  let t12;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = async (elements, t13) => {
      const durationMs = t13 === undefined ? ARRIVAL_PULSE_MS : t13;
      clearActiveTargets();
      const uniqueTargets = [...new Set(elements.filter(_temp3))];
      if (uniqueTargets.length === 0) {
        return;
      }
      activeTargetsRef.current = uniqueTargets;
      for (const element_1 of uniqueTargets) {
        element_1.classList.add(GHOST_CURSOR_TARGET_ACTIVE_CLASS_NAME);
      }
      await wait(durationMs);
      for (const element_2 of uniqueTargets) {
        element_2.classList.remove(GHOST_CURSOR_TARGET_ACTIVE_CLASS_NAME);
      }
      if (activeTargetsRef.current === uniqueTargets) {
        activeTargetsRef.current = [];
      }
    };
    $[10] = t12;
  } else {
    t12 = $[10];
  }
  const pulseTargets = t12;
  let t13;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = t14 => {
      const from = t14 === undefined ? "pointer" : t14;
      if (typeof from === "object") {
        return from;
      }
      if (from === "previous" && scriptedPointerRef.current) {
        return scriptedPointerRef.current;
      }
      if (trackedPointerRef.current) {
        return trackedPointerRef.current;
      }
      return getViewportFallbackPoint();
    };
    $[11] = t13;
  } else {
    t13 = $[11];
  }
  const resolveOrigin = t13;
  let t14;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = (phase, position, durationMs_0, t15) => {
      const easing = t15 === undefined ? "smooth" : t15;
      scriptedPointerRef.current = position;
      setCursorState(current_0 => ({
        main: {
          ...current_0.main,
          easing,
          phase,
          position,
          durationMs: durationMs_0
        },
        satellites: []
      }));
    };
    $[12] = t14;
  } else {
    t14 = $[12];
  }
  const updateMainCursor = t14;
  let t15;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = async (origin, target, durationMs_1, easing_0) => {
      updateMainCursor("traveling", origin, 0, easing_0);
      if (durationMs_1 <= 0) {
        updateMainCursor("traveling", target, 0, easing_0);
        return;
      }
      await waitForNextAnimationFrame();
      updateMainCursor("traveling", target, durationMs_1, easing_0);
      await wait(durationMs_1);
    };
    $[13] = t15;
  } else {
    t15 = $[13];
  }
  const animateMainCursorTravel = t15;
  let t16;
  let t17;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      const syncReducedMotion = () => {
        reducedMotionRef.current = mediaQuery.matches;
      };
      const handlePointerMove = event => {
        trackedPointerRef.current = createPoint(event.clientX, event.clientY);
      };
      const handleWindowBlur = () => {
        trackedPointerRef.current = null;
      };
      syncReducedMotion();
      mediaQuery.addEventListener("change", syncReducedMotion);
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("blur", handleWindowBlur);
      return () => {
        mediaQuery.removeEventListener("change", syncReducedMotion);
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("blur", handleWindowBlur);
      };
    };
    t17 = [];
    $[14] = t16;
    $[15] = t17;
  } else {
    t16 = $[14];
    t17 = $[15];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return;
      }
      const passiveListener = {
        passive: true
      };
      const captureListener = {
        capture: true,
        passive: true
      };
      window.addEventListener("wheel", dismissCursors, passiveListener);
      window.addEventListener("touchmove", dismissCursors, passiveListener);
      window.addEventListener("scroll", dismissCursors, passiveListener);
      document.addEventListener("scroll", dismissCursors, captureListener);
      return () => {
        window.removeEventListener("wheel", dismissCursors, passiveListener);
        window.removeEventListener("touchmove", dismissCursors, passiveListener);
        window.removeEventListener("scroll", dismissCursors, passiveListener);
        document.removeEventListener("scroll", dismissCursors, captureListener);
      };
    };
    t19 = [dismissCursors];
    $[16] = t18;
    $[17] = t19;
  } else {
    t18 = $[16];
    t19 = $[17];
  }
  useEffect(t18, t19);
  let t20;
  let t21;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => () => {
      clearHideTimer();
      clearActiveTargets();
    };
    t21 = [clearActiveTargets, clearHideTimer];
    $[18] = t20;
    $[19] = t21;
  } else {
    t20 = $[18];
    t21 = $[19];
  }
  useEffect(t20, t21);
  let t22;
  if ($[20] !== scheduleHide || $[21] !== scrollSettleMs || $[22] !== viewportPadding) {
    t22 = async (target_0, operation, options) => {
      clearHideTimer();
      clearActiveTargets();
      const motion = normalizeMotionOptions(options);
      const stop = resolveTargetStop(target_0);
      if (!stop) {
        return operation();
      }
      const targetElement = stop.element;
      const pulseElement = stop.pulseElement;
      const targetPoint = stop.point;
      if (reducedMotionRef.current) {
        ;
        try {
          const result = await operation();
          updateMainCursor("arrived", targetPoint, ARRIVAL_PULSE_MS);
          await pulseTargets([pulseElement]);
          await wait(STEP_HOLD_MS);
          scheduleHide();
          return result;
        } catch (t23) {
          const error = t23;
          updateMainCursor("error", targetPoint, ARRIVAL_PULSE_MS);
          scheduleHide();
          await wait(ARRIVAL_PULSE_MS);
          throw error;
        }
      }
      if (targetElement && isOutsidePaddedViewport(targetElement, viewportPadding)) {
        targetElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
        await wait(scrollSettleMs);
      }
      const resolvedStop = resolveTargetStop(target_0) ?? stop;
      const origin_0 = resolveOrigin(motion.from);
      const durationMs_2 = getTravelDuration(origin_0, resolvedStop.point);
      await animateMainCursorTravel(origin_0, resolvedStop.point, durationMs_2, motion.easing);
      ;
      try {
        const result_0 = await operation();
        updateMainCursor("arrived", resolvedStop.point, ARRIVAL_PULSE_MS);
        await pulseTargets([resolvedStop.pulseElement]);
        await wait(STEP_HOLD_MS);
        scheduleHide();
        return result_0;
      } catch (t24) {
        const error_0 = t24;
        updateMainCursor("error", resolvedStop.point, ARRIVAL_PULSE_MS);
        scheduleHide();
        await wait(ARRIVAL_PULSE_MS);
        throw error_0;
      }
    };
    $[20] = scheduleHide;
    $[21] = scrollSettleMs;
    $[22] = viewportPadding;
    $[23] = t22;
  } else {
    t22 = $[23];
  }
  const runSingle = t22;
  let t23;
  if ($[24] !== scrollSettleMs || $[25] !== viewportPadding) {
    t23 = async (items, resolveTarget, operation_0, options_0) => {
      clearHideTimer();
      clearActiveTargets();
      const motion_0 = normalizeMotionOptions(options_0);
      let resolvedTargets = items.map((item, index) => resolveTargetStop(resolveTarget(item, index) ?? {}));
      const resolvedElements = resolvedTargets.flatMap(_temp4);
      if (!reducedMotionRef.current && resolvedElements.some(element_3 => isOutsidePaddedViewport(element_3, viewportPadding))) {
        getBatchScrollTarget(resolvedElements)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center"
        });
        await wait(scrollSettleMs);
        resolvedTargets = items.map((item_0, index_0) => resolveTargetStop(resolveTarget(item_0, index_0) ?? {}));
      }
      const results = [];
      let currentPoint = resolveOrigin(motion_0.from);
      let hasResolvedTarget = false;
      for (const [index_1, item_1] of items.entries()) {
        const target_2 = resolvedTargets[index_1];
        if (!target_2) {
          results.push(await operation_0(item_1, index_1));
          continue;
        }
        hasResolvedTarget = true;
        if (reducedMotionRef.current) {
          updateMainCursor("arrived", target_2.point, FAST_BATCH_PULSE_MS);
        } else {
          const durationMs_3 = getFastBatchTravelDuration(currentPoint, target_2.point);
          await animateMainCursorTravel(currentPoint, target_2.point, durationMs_3, motion_0.easing);
          updateMainCursor("arrived", target_2.point, FAST_BATCH_PULSE_MS);
        }
        currentPoint = target_2.point;
        ;
        try {
          results.push(await operation_0(item_1, index_1));
        } catch (t24) {
          const error_1 = t24;
          updateMainCursor("error", target_2.point, ARRIVAL_PULSE_MS);
          await wait(ARRIVAL_PULSE_MS);
          throw error_1;
        }
        await pulseTargets([target_2.pulseElement], FAST_BATCH_PULSE_MS);
      }
      if (!hasResolvedTarget) {
        hideAllCursors();
        return results;
      }
      await wait(FAST_BATCH_FINAL_HOLD_MS);
      hideAllCursors();
      return results;
    };
    $[24] = scrollSettleMs;
    $[25] = viewportPadding;
    $[26] = t23;
  } else {
    t23 = $[26];
  }
  const runEachInternal = t23;
  let t24;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = run => {
      const queuedRun = queueRef.current.then(run, run);
      queueRef.current = queuedRun.then(_temp5, _temp6);
      return queuedRun;
    };
    $[27] = t24;
  } else {
    t24 = $[27];
  }
  const enqueueRun = t24;
  let t25;
  if ($[28] !== runSingle) {
    t25 = async (target_3, operation_1, options_1) => enqueueRun(() => runSingle(target_3, operation_1, options_1));
    $[28] = runSingle;
    $[29] = t25;
  } else {
    t25 = $[29];
  }
  const run_0 = t25;
  let t26;
  if ($[30] !== runEachInternal) {
    t26 = async (items_0, resolveTarget_0, operation_2, options_2) => enqueueRun(() => runEachInternal(items_0, resolveTarget_0, operation_2, options_2));
    $[30] = runEachInternal;
    $[31] = t26;
  } else {
    t26 = $[31];
  }
  const runEach = t26;
  let t27;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = () => {
      dismissCursors();
    };
    $[32] = t27;
  } else {
    t27 = $[32];
  }
  const hide = t27;
  let t28;
  if ($[33] !== cursorState || $[34] !== runEach || $[35] !== run_0) {
    t28 = {
      cursorState,
      hide,
      run: run_0,
      runEach
    };
    $[33] = cursorState;
    $[34] = runEach;
    $[35] = run_0;
    $[36] = t28;
  } else {
    t28 = $[36];
  }
  return t28;
}
function _temp6() {}
function _temp5() {}
function _temp4(target_1) {
  return target_1?.element ? [target_1.element] : [];
}
function _temp3(element_0) {
  return !!element_0;
}
function _temp2(current) {
  return {
    main: {
      ...current.main,
      phase: "hidden",
      durationMs: ARRIVAL_PULSE_MS
    },
    satellites: []
  };
}
function _temp() {
  return {
    main: {
      id: "main",
      role: "main",
      phase: "hidden",
      position: getViewportFallbackPoint(),
      durationMs: 0,
      easing: "smooth"
    },
    satellites: []
  };
}